import { useState, useEffect, useRef } from 'react';
import { deleteEventIdea, toggleIdeaLike } from '../lib/supabase';
import { firstName } from '../helpers';
import Modal from '../ui/Modal';

export default function IdeasSection({ ideas, musicians, currentUser, canManage, onRefresh }) {
  const [tooltipId, setTooltipId] = useState(null);
  const [deleteIdea, setDeleteIdea] = useState(null); // idea da eliminare
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tooltipRef = useRef(null);

  // Chiudi tooltip cliccando fuori
  useEffect(() => {
    if (!tooltipId) return;
    const handler = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltipId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [tooltipId]);

  const handleLike = async (idea) => {
    const hasLiked = (idea.idea_likes || []).some(l => l.musician_id === currentUser.id);
    await toggleIdeaLike(idea.id, currentUser.id, hasLiked);
    await onRefresh();
  };

  const handleDeleteConfirmed = async () => {
    await deleteEventIdea(deleteIdea.id);
    setDeleteIdea(null);
    setConfirmDelete(false);
    await onRefresh();
  };

  return (
    <div>
      {ideas.length === 0 && (
        <div style={{ fontSize: 17, color: 'var(--muted)', padding: '8px 0 14px' }}>
          Ancora nessuna idea. Sii il primo!
        </div>
      )}

      {ideas.map(idea => {
        const likes = idea.idea_likes || [];
        const hasLiked = likes.some(l => l.musician_id === currentUser.id);
        const author = musicians.find(m => m.id === idea.musician_id);
        const isTooltipOpen = tooltipId === idea.id;
        const canDelete = canManage || idea.musician_id === currentUser.id;

        return (
          <div key={idea.id} style={{
            padding: '11px 0',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            {/* Testo — cliccabile solo per chi può eliminare */}
            <div
              style={{ flex: 1, minWidth: 0, cursor: canDelete ? 'pointer' : 'default' }}
              onClick={() => { if (canDelete) { setDeleteIdea(idea); setConfirmDelete(false); } }}>
              <div style={{ fontSize: 17, color: 'var(--text)', lineHeight: 1.45 }}>{idea.text}</div>
              <div style={{ fontSize: 16, color: 'var(--muted)', marginTop: 3 }}>
                {author ? firstName(author.name) : '?'}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              {/* Cuore — solo toggle like */}
              <button
                onClick={() => handleLike(idea)}
                style={{
                  background: hasLiked ? 'rgba(237,77,29,0.12)' : 'rgba(255,255,255,0.04)',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 9px',
                  minHeight: 36,
                  color: hasLiked ? 'var(--brand)' : 'var(--muted)',
                  transition: 'background .15s',
                }}>
                <span style={{ fontSize: 19, lineHeight: 1 }}>{hasLiked ? '♥' : '♡'}</span>
              </button>

              {/* Contatore cliccabile — apre tooltip */}
              {likes.length > 0 && (
                <div style={{ position: 'relative' }} ref={isTooltipOpen ? tooltipRef : null}>
                  <button
                    onClick={() => setTooltipId(isTooltipOpen ? null : idea.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 16,
                      fontWeight: 700,
                      color: hasLiked ? 'var(--brand)' : 'var(--muted)',
                      padding: '6px 4px',
                      minHeight: 36,
                    }}>
                    {likes.length}
                  </button>
                  {isTooltipOpen && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 'calc(100% + 4px)',
                      background: '#2A2520',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '8px 12px',
                      minWidth: 110,
                      zIndex: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                    }}>
                      {likes.map(l => {
                        const m = musicians.find(mu => mu.id === l.musician_id);
                        return (
                          <div key={l.musician_id} style={{ fontSize: 16, color: 'var(--text)', padding: '3px 0', whiteSpace: 'nowrap' }}>
                            {m ? firstName(m.name) : '?'}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal elimina idea */}
      {deleteIdea && (
        <Modal onClose={() => { setDeleteIdea(null); setConfirmDelete(false); }}>
          <div className="modal-title">Idea proposta</div>
          <div style={{
            fontSize: 18, color: 'var(--text)', lineHeight: 1.5,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '12px 14px', marginBottom: 20,
          }}>
            {deleteIdea.text}
          </div>
          {!confirmDelete
            ? <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setDeleteIdea(null); setConfirmDelete(false); }}>Chiudi</button>
                <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>Elimina</button>
              </div>
            : <>
                <div style={{ fontSize: 16, color: 'var(--muted)', marginBottom: 16 }}>
                  Sicuro di voler eliminare questa idea?
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Annulla</button>
                  <button className="btn btn-danger" onClick={handleDeleteConfirmed}>Sì, elimina</button>
                </div>
              </>
          }
        </Modal>
      )}

    </div>
  );
}
